import { TrendingUp, Activity, Target, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { clsx } from 'clsx';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { PerformanceDashboard } from '@/api/reports.api';

interface PerformanceCardProps {
  perf?: PerformanceDashboard;
}

export function PerformanceCard({ perf }: PerformanceCardProps) {
  const performance = perf?.performance;
  const monthly = performance?.monthly;
  const weeklyChart = perf?.weeklyChart ?? [];

  const avgTx = performance?.avgTransactionSize ?? 0;
  const commissionRate = performance?.commissionRate ?? 0;
  const commissions = monthly?.commissions ?? 0;
  const goal = monthly?.goal ?? 100;
  const progress = monthly?.progress ?? 0;
  const progressPct = Math.min((progress / goal) * 100, 100);
  const commissionChange = performance?.commissionChange;

  const metrics = [
    {
      label: 'Promedio por Tx',
      value: `$${avgTx.toLocaleString()}`,
      icon: Activity,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
    },
    {
      label: 'Tasa de Comisión',
      value: `${commissionRate.toFixed(2)}%`,
      icon: Target,
      color: 'text-purple-600',
      bg: 'bg-purple-50',
    },
  ];

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-purple-600" />
            <h3 className="text-lg font-semibold text-gray-900">Rendimiento</h3>
          </div>
          {perf?.period && (
            <span className="text-xs text-gray-500 capitalize">
              {perf.period.monthName} {perf.period.year}
            </span>
          )}
        </div>
      </div>

      <div className="p-6 space-y-4">
        {/* Comisiones del mes con variación */}
        <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Comisiones del Mes</p>
            <p className="text-xl font-bold text-gray-900">${commissions.toLocaleString()}</p>
          </div>
          {commissionChange !== undefined && (
            <div
              className={clsx(
                'flex items-center gap-1 text-sm font-semibold',
                commissionChange >= 0 ? 'text-green-600' : 'text-red-600'
              )}
            >
              {commissionChange >= 0 ? (
                <ArrowUpRight className="w-4 h-4" />
              ) : (
                <ArrowDownRight className="w-4 h-4" />
              )}
              {Math.abs(commissionChange).toFixed(0)}%
            </div>
          )}
        </div>

        {/* Metric rows */}
        {metrics.map((metric) => {
          const Icon = metric.icon;
          return (
            <div key={metric.label} className="flex items-center gap-3">
              <div className={clsx('p-2 rounded-lg', metric.bg)}>
                <Icon className={clsx('w-5 h-5', metric.color)} />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-600">{metric.label}</p>
                <p className="text-lg font-bold text-gray-900">{metric.value}</p>
              </div>
            </div>
          );
        })}

        {/* Progress to Goal */}
        <div className="pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Meta Mensual</span>
            <span className="text-sm font-medium text-gray-900">
              {progress} / {goal}
            </span>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={clsx(
                'h-full transition-all duration-500 rounded-full',
                progressPct >= 100
                  ? 'bg-green-500'
                  : progressPct >= 50
                    ? 'bg-purple-500'
                    : 'bg-purple-400'
              )}
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <p className="text-xs text-gray-400 mt-1 text-right">{progressPct.toFixed(0)}%</p>
        </div>

        {/* Weekly Chart */}
        {weeklyChart.length > 0 && (
          <div className="pt-4 border-t border-gray-200">
            <p className="text-sm font-medium text-gray-700 mb-3">Actividad Semanal</p>
            <ResponsiveContainer width="100%" height={120}>
              <BarChart data={weeklyChart} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip
                  formatter={(value: number) => `$${value.toLocaleString()}`}
                  labelStyle={{ fontSize: 11 }}
                />
                <Bar dataKey="cashIn" name="Depósitos" fill="#10b981" radius={[3, 3, 0, 0]} />
                <Bar dataKey="cashOut" name="Retiros" fill="#ef4444" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

export default PerformanceCard;
